/* eslint-disable @next/next/no-img-element */
// Next's image component does not allow constructing your own srcSet
// https://github.com/vercel/next.js/blob/canary/packages/next/client/image.tsx

import React, { useEffect, useState } from "react";
import cx from "classnames";

import styles from "./Image.module.scss";
import Head from "next/head";
import { useInViewCompat } from "utils/useInViewCompat";

let isNativeLazySupported: boolean | undefined = undefined;

/**
 * Source: https://github.com/charlie-tango/hooks/blob/master/packages/useNativeLazyLoading/src/useNativeLazyLoading.ts
 */
const useNativeLazyLoading = () => {
  const [supported, setSupported] = useState<boolean | undefined>(
    isNativeLazySupported
  );

  useEffect(() => {
    if (isNativeLazySupported === undefined) {
      isNativeLazySupported = "loading" in HTMLImageElement.prototype;
      setSupported(isNativeLazySupported);
    }
  }, []);

  return supported;
};

type SharedProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  alt: string;
  className?: string;
  srcSet?: string;
  sizes?: string;
};

interface LazyProps extends SharedProps {
  lazy: true;
  width: number;
  height: number;
}

interface NotLazyProps extends SharedProps {
  lazy: false;
  preload?: boolean;
}

type Props = LazyProps | NotLazyProps;

class ImageBoundary extends React.Component<Props, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { alt, className, lazy, ...props } = this.props;

    if (this.state.hasError) {
      return (
        <img alt={alt} className={cx(styles.root, className)} {...props} />
      );
    }

    return this.props.children;
  }
}

const LazyImage = ({
  src,
  sizes,
  srcSet,
  alt,
  className,
  // Disable the unused variable warning on the next line to keep `lazy` out of
  // the props object that gets spread into <img>
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  lazy,
  ...props
}: LazyProps) => {
  const [loaded, setLoaded] = useState(false);

  const supportsNativeLazy = useNativeLazyLoading();

  const [ref, inView] = useInViewCompat({
    rootMargin: "200px",
    threshold: 0,
    triggerOnce: true,
  });

  const aspectRatioPlaceholder = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${props.width} ${props.height}' /%3E`;

  return (
    <img
      ref={!supportsNativeLazy ? ref : undefined}
      alt={alt}
      loading="lazy"
      className={cx(
        styles.root,
        styles.lazy,
        loaded && styles.loaded,
        className
      )}
      onLoad={() => setLoaded(true)}
      sizes={sizes}
      srcSet={inView || supportsNativeLazy ? srcSet : undefined}
      src={inView || supportsNativeLazy ? src : aspectRatioPlaceholder}
      {...props}
    />
  );
};

const NonLazyImage = ({
  preload,
  src,
  sizes,
  srcSet,
  alt,
  className,
  // See the comment in the <LazyImage> props above about this suppressed warning.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  lazy,
  ...props
}: NotLazyProps) => (
  <>
    {preload && (
      <Head>
        <link
          key={`preload-${src}`}
          rel="preload"
          as="image"
          href={src}
          imagesrcset={srcSet}
          imagesizes={sizes}
        />
      </Head>
    )}
    <img
      alt={alt}
      className={cx(styles.root, className)}
      sizes={sizes}
      srcSet={srcSet}
      src={src}
      {...props}
    />
  </>
);

const Image = (props: Props) => {
  return (
    <ImageBoundary {...props}>
      {props.lazy ? <LazyImage {...props} /> : <NonLazyImage {...props} />}
    </ImageBoundary>
  );
};

export default Image;
